<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo } from '../../lib/shortcuts'
import { fmt } from '../../stores'
import { state, closeSheet, toast } from '../../stores/state'
import { count } from '../../stores/usage'
import { t } from '../../stores/i18n'
import { addGoal, deleteGoal, updateGoal } from '../../stores/goals'
import { goalProgress } from '../../lib/analytics'
import { currencySymbol, parseAmount, round2 } from '../../lib/money'
import { SAVING_CADENCES, cadenceKey } from '../../lib/savings'
import { addMonths, fullDay, today as todayISO } from '../../lib/dates'

const props = defineProps({ payload: { type: Object, default: () => ({}) } })

const existing = computed(() =>
  props.payload?.id ? state.goals.find((g) => g.id === props.payload.id) : null,
)

const form = reactive({
  name: '',
  target_amount: '',
  initial_amount: '',
  pace_mode: 'by_date',
  target_date: addMonths(todayISO(), 12),
  monthly_amount: '',
  cadence: 'monthly',
  start_date: todayISO(),
  archived: false,
})
const error = ref('')
const confirmDelete = ref(false)

onMounted(() => {
  const g = existing.value
  if (g)
    Object.assign(form, {
      ...g,
      target_amount: String(g.target_amount),
      initial_amount: String(g.initial_amount ?? 0),
      monthly_amount: g.monthly_amount ? String(g.monthly_amount) : '',
      target_date: g.target_date ?? addMonths(todayISO(), 12),
      cadence: g.cadence ?? 'monthly',
      start_date: g.start_date ?? String(g.created_at ?? todayISO()).slice(0, 10),
      archived: !!g.archived,
    })
})

const symbol = computed(() => currencySymbol(state.settings.currency))

/** Live pacing readout, computed from the same code the panel uses. */
const preview = computed(() => {
  const target = parseAmount(form.target_amount)
  if (!Number.isFinite(target) || target <= 0) return null
  const draft = {
    id: existing.value?.id ?? '__draft__',
    name: form.name,
    target_amount: target,
    initial_amount: parseAmount(form.initial_amount) || 0,
    pace_mode: form.pace_mode,
    target_date: form.pace_mode === 'by_date' ? form.target_date : null,
    monthly_amount: parseAmount(form.monthly_amount) || 0,
    cadence: form.cadence,
    start_date: form.start_date,
    created_at: existing.value?.created_at ?? todayISO(),
  }
  return goalProgress(draft, todayISO())
})

const instalment = computed(() => t(cadenceKey(form.cadence)).toLowerCase())

async function save() {
  const target = parseAmount(form.target_amount)
  if (!form.name.trim()) return (error.value = t('goalSheet.needName'))
  if (!Number.isFinite(target) || target <= 0) return (error.value = t('goalSheet.needTarget'))
  if (form.pace_mode === 'by_amount' && !(parseAmount(form.monthly_amount) > 0))
    return (error.value = t('goalSheet.needInstalment'))
  const payload = {
    ...form,
    target_amount: target,
    initial_amount: round2(parseAmount(form.initial_amount) || 0),
    monthly_amount: parseAmount(form.monthly_amount) || 0,
  }
  if (existing.value) await updateGoal(existing.value.id, payload)
  else {
    count('goal.add')
    await addGoal(payload)
  }
  toast(t(existing.value ? 'goalSheet.updated' : 'goalSheet.added'), 'good')
  closeSheet()
}

async function destroy() {
  if (!confirmDelete.value) return (confirmDelete.value = true)
  await deleteGoal(existing.value.id)
  toast(t('goalSheet.deleted'))
  closeSheet()
}
</script>

<template>
  <Sheet
    :title="t(existing ? 'goalSheet.edit' : 'goalSheet.new')"
    :subtitle="t('goalSheet.subtitle')"
    @close="closeSheet"
  >
    <div class="grid gap-4">
      <div>
        <label class="label" for="gl-name">{{ t('goalSheet.goal') }}</label>
        <input id="gl-name" v-model="form.name" class="input" :placeholder="t('goalSheet.goalPlaceholder')" />
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="label" for="gl-target">{{ t('goalSheet.target') }}</label>
          <div class="relative">
            <span class="absolute top-1/2 left-3 -translate-y-1/2 text-faint">{{ symbol }}</span>
            <input id="gl-target" v-model="form.target_amount" class="input tnum pl-7" inputmode="decimal" placeholder="4000" />
          </div>
        </div>
        <div>
          <label class="label" for="gl-initial">{{ t('goalSheet.alreadySaved') }}</label>
          <div class="relative">
            <span class="absolute top-1/2 left-3 -translate-y-1/2 text-faint">{{ symbol }}</span>
            <input id="gl-initial" v-model="form.initial_amount" class="input tnum pl-7" inputmode="decimal" placeholder="0" />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-1 rounded-xl bg-raised p-1">
        <button
          v-for="m in [
            { value: 'by_date', label: t('goalSheet.byDate') },
            { value: 'by_amount', label: t('goalSheet.byAmount') },
          ]"
          :key="m.value"
          class="rounded-lg py-2 text-[13.5px] font-semibold transition"
          :class="form.pace_mode === m.value ? 'bg-surface text-text shadow-sm' : 'text-muted'"
          @click="form.pace_mode = m.value"
        >
          {{ m.label }}
        </button>
      </div>

      <div v-if="form.pace_mode === 'by_date'">
        <label class="label" for="gl-date">{{ t('goalSheet.reachBy') }}</label>
        <input id="gl-date" v-model="form.target_date" type="date" class="input" />
      </div>
      <div v-else>
        <label class="label" for="gl-monthly">{{ t('goalSheet.putAside', { cadence: instalment }) }}</label>
        <div class="relative">
          <span class="absolute top-1/2 left-3 -translate-y-1/2 text-faint">{{ symbol }}</span>
          <input id="gl-monthly" v-model="form.monthly_amount" class="input tnum pl-7" inputmode="decimal" placeholder="200" />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="label" for="gl-cadence">{{ t('goalSheet.setAside') }}</label>
          <select id="gl-cadence" v-model="form.cadence" class="input">
            <option v-for="c in SAVING_CADENCES" :key="c.value" :value="c.value">
              {{ t(cadenceKey(c.value)) }}
            </option>
          </select>
        </div>
        <div>
          <label class="label" for="gl-start">{{ t('goalSheet.starting') }}</label>
          <input id="gl-start" v-model="form.start_date" type="date" class="input" />
        </div>
      </div>

      <div v-if="preview" class="rounded-xl border border-goal/30 bg-goal-soft px-3.5 py-3">
        <p class="tnum text-[13.5px] leading-relaxed">
          <template v-if="form.pace_mode === 'by_date'">
            {{ t('goalSheet.setsAside', { amount: fmt(preview.perInstalment), cadence: instalment }) }}
            — {{ t('goalSheet.times', { count: preview.instalments }) }}<template v-if="preview.end">{{
              t('goalSheet.lastOn', { day: fullDay(preview.end) })
            }}</template>.
          </template>
          <template v-else>
            {{
              t('goalSheet.reaches', {
                day: preview.end ? fullDay(preview.end) : t('goalSheet.dayOne'),
                count: preview.instalments,
              })
            }}
          </template>
        </p>
        <p class="tnum mt-1 text-[12px] text-muted">
          {{ t('goalSheet.countsAsSaved', { amount: fmt(preview.setAside) }) }}
        </p>
      </div>

      <label v-if="existing" class="flex items-center gap-2.5 text-[13.5px]">
        <input v-model="form.archived" type="checkbox" class="size-4 accent-[var(--c-accent)]" />
        {{ t('goalSheet.archive') }}
      </label>

      <p v-if="error" class="flex items-center gap-2 text-[13px] text-danger">
        <Icon name="alert" :size="15" /> {{ error }}
      </p>
    </div>

    <template #footer>
      <button v-if="existing" class="btn-danger" @click="destroy">
        <Icon name="trash" :size="16" />
        {{ confirmDelete ? t('common.confirmDelete') : t('common.delete') }}
      </button>
      <div class="flex-1" />
      <button class="btn-soft" @click="closeSheet">{{ t('common.cancel') }}</button>
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" class="btn-primary" @click="save"><Icon name="check" :size="16" /> {{ t('common.save') }}<Kbd combo="mod+enter" /></button>
    </template>
  </Sheet>
</template>
